<template>
  <section class="mb-panel mb-conn">
    <header class="mb-panel__head">
      <h2>Подключение к матчу</h2>
      <span class="mb-muted mb-small">Игровой ID судьи → матчи, где он в комнате → источник данных</span>
    </header>

    <div class="mb-panel__body mb-stack">
      <form class="mb-conn__form" @submit.prevent="submit">
        <div class="mb-field mb-conn__id">
          <label for="mb-judge-id">Игровой ID судьи</label>
          <input id="mb-judge-id"
                 ref="input"
                 v-model.trim="input"
                 class="mb-input mb-mono"
                 inputmode="numeric"
                 autocomplete="off"
                 placeholder="например 38332">
        </div>
        <button type="submit" class="mb-btn mb-btn--lg" :disabled="loading">Найти матчи</button>
        <span v-if="inputError" class="mb-error-text">{{ inputError }}</span>
      </form>

      <div v-if="recentChips.length || judges.length" class="mb-conn__recent">
        <span class="mb-muted mb-small">Недавние:</span>
        <button v-for="chip in recentChips"
                :key="chip.judge_id"
                type="button"
                :class="['mb-chip', {'is-active': chip.judge_id === judgeId}]"
                @click="useJudge(chip.judge_id)">
          <span class="mb-mono">{{ chip.judge_id }}</span>
          <span v-if="chip.name" class="mb-ellipsis">{{ chip.name }}</span>
        </button>
        <router-link class="mb-link" :to="{name: 'mlbb_broadcast_judges'}">Справочник судей</router-link>
      </div>

      <template v-if="judgeId">
        <div class="mb-conn__judge">
          <span>Судья <b class="mb-mono">{{ judgeId }}</b></span>
          <template v-if="knownJudge">
            <span>· {{ knownJudge.name }}</span>
            <span v-if="!knownJudge.is_active" class="mb-badge mb-badge--warning">неактивен — не опрашивается</span>
          </template>
          <button v-else type="button" class="mb-link" :disabled="savingJudge" @click="saveJudge">
            сохранить в справочник
          </button>
        </div>

        <div class="mb-conn__toolbar">
          <input v-model.trim="filters.q" class="mb-input mb-input--sm" placeholder="Команда, комната, battle id">
          <select v-model="filters.state" class="mb-select mb-select--sm">
            <option v-for="o in stateFilters" :key="o.value" :value="o.value">{{ o.text }}</option>
          </select>
          <label class="mb-check">
            <input v-model="filters.unlinked" type="checkbox">
            только непривязанные
          </label>
          <span class="mb-spacer"></span>
          <span v-if="loading" class="mb-loading">Обновляем</span>
          <button type="button" class="mb-btn mb-btn--ghost mb-btn--sm" :disabled="loading" @click="load">
            <i class="fa fa-refresh"></i> Обновить
          </button>
        </div>

        <div v-if="loadError" class="mb-alert mb-alert--danger">
          Не удалось загрузить матчи: {{ loadError }}
          <button type="button" class="mb-link" @click="load">повторить</button>
        </div>

        <div v-else-if="!loading && !visibleBattles.length" class="mb-empty">
          <b>{{ hasFilters ? 'Ничего не найдено' : 'По этому судье матчей нет' }}</b>
          {{ hasFilters ? 'Снимите фильтры.' : 'Данные появляются, только когда судья зашёл в комнату стримового матча.' }}
        </div>

        <div v-else class="mb-table-wrap">
          <table class="mb-table">
            <thead>
            <tr>
              <th>Команды</th>
              <th>Статус матча</th>
              <th>Синхронизация с Moonton</th>
              <th>Матч BIGPLAY</th>
              <th>Обновлено</th>
              <th></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="b in visibleBattles" :key="b.id" :class="{'is-selected': b.id === sourceBattleId}">
              <td class="mb-conn__teams">
                <b class="mb-ellipsis">{{ battleTitle(b) }}</b>
                <span class="mb-muted mb-small mb-ellipsis">
                  <span class="mb-mono">…{{ String(b.battle_id || '').slice(-8) }}</span>
                  <template v-if="b.room_name"> · {{ b.room_name }}</template>
                  · {{ formatDateTime(b.started_at || b.reported_at) }}
                </span>
              </td>
              <td>
                <span :class="['mb-badge', 'mb-badge--' + stateVariant(b.state)]">{{ stateLabel(b.state) }}</span>
                <div v-if="b.state === 'play'" class="mb-muted mb-small">
                  {{ formatGameTime(b.game_time) }}<template v-if="b.paused"> · пауза</template>
                </div>
                <div v-else-if="b.state === 'end' && b.win_camp" class="mb-muted mb-small">
                  победа: {{ campShortName(campByNumber(b, b.win_camp)) }}
                </div>
              </td>
              <td class="mb-conn__sync">
                <span class="mb-nowrap">
                  <span :class="['mb-dot', 'mb-dot--' + syncStatus(b, now).level]"></span>
                  {{ syncStatus(b, now).text }}
                </span>
                <div v-if="b.last_sync_error" class="mb-error-text">{{ b.last_sync_error }}</div>
                <button v-if="b.last_sync_error || b.poll_enabled === false"
                        type="button"
                        class="mb-link"
                        :disabled="busyId === b.id"
                        @click="resync(b)">повторить синхронизацию</button>
              </td>
              <td>
                <template v-if="b.group_id">
                  <span class="mb-badge mb-badge--success">#{{ b.group_id }}</span>
                  <div><button type="button" class="mb-link mb-link--danger" :disabled="busyId === b.id" @click="unlink(b)">отвязать</button></div>
                </template>
                <template v-else>
                  <span class="mb-badge mb-badge--warning">не привязан</span>
                  <div><button type="button" class="mb-link" @click="$emit('link', b)">привязать</button></div>
                </template>
              </td>
              <td class="mb-nowrap mb-small">{{ timeAgo(lastUpdateAt(b), now) }}</td>
              <td class="mb-nowrap">
                <span v-if="b.id === sourceBattleId" class="mb-badge mb-badge--primary">источник</span>
                <button v-else type="button" class="mb-btn mb-btn--sm" @click="$emit('select', b)">
                  Сделать источником
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <div v-if="lastPage > 1" class="mb-conn__pager">
          <b-pagination v-model="page" class="my-0" size="sm" :total-rows="total" :per-page="perPage" :limit="5"/>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import api from '@/api/api'
import {connectToWs, onMessage, onStatus, subscribe, unsubscribe} from '@/services/websocket'
import {
  STATE_FILTERS,
  battleTitle,
  campByNumber,
  campShortName,
  campTeamName,
  formatDateTime,
  formatGameTime,
  lastUpdateAt,
  matchesStateFilter,
  parseJudgeId,
  readRecentJudges,
  stateLabel,
  stateVariant,
  syncStatus,
  timeAgo,
} from '@/common/mlbbBroadcast'

// Список по судье живёт по WS: бэк присылает новый батл судьи или изменения строки списка
// (стадия, время и счёт, победитель, пауза, привязка, poll_enabled, last_sync_error)
export const JUDGE_BATTLE_UPDATED_EVENT = 'mlbb-judge.battle-updated'
export const judgeBattlesChannel = (judgeId) => `mlbb-judges.${judgeId}.battles`

export default {
  name: 'MbMatchesByJudge',
  props: {
    judgeId: {type: Number, default: null},
    sourceBattleId: {type: Number, default: null},
    judges: {type: Array, default: () => []},
    now: {type: Number, default: () => Date.now()},
    // меняется снаружи, когда список надо перечитать (после привязки и т.п.)
    reloadKey: {type: Number, default: 0},
  },
  data() {
    return {
      input: this.judgeId ? String(this.judgeId) : '',
      inputError: '',
      battles: [],
      loading: false,
      loadError: '',
      page: 1,
      total: 0,
      perPage: 30,
      lastPage: 1,
      filters: {q: '', state: '', unlinked: false},
      busyId: null,
      savingJudge: false,
      recent: readRecentJudges(),
      stateFilters: STATE_FILTERS,
      channel: null,
      loaded: false,
      offMessage: null,
      offStatus: null,
    }
  },
  computed: {
    knownJudge() {
      return this.judges.find((j) => j.judge_id === this.judgeId) || null
    },
    recentChips() {
      // сначала недавние из этого браузера, затем активные из справочника
      const byId = new Map(this.judges.map((j) => [j.judge_id, j]))
      const ids = [...this.recent, ...this.judges.filter((j) => j.is_active).map((j) => j.judge_id)]
      return [...new Set(ids)].slice(0, 8).map((id) => ({judge_id: id, name: byId.get(id)?.name || ''}))
    },
    hasFilters() {
      return !!(this.filters.q || this.filters.state || this.filters.unlinked)
    },
    visibleBattles() {
      const q = this.filters.q.toLowerCase()
      return this.battles
          .filter((b) => matchesStateFilter(b, this.filters.state))
          .filter((b) => {
            if (!q) return true
            const hay = [b.battle_id, b.room_name, b.group_id, ...(b.camps || []).flatMap((c) => [
              c.in_game_team_name, c.in_game_team_short_name, campTeamName(c),
            ])].join(' ').toLowerCase()
            return hay.includes(q)
          })
    },
  },
  watch: {
    judgeId(id) {
      this.input = id ? String(id) : ''
      this.recent = readRecentJudges()
      this.loaded = false
      this.battles = []
      this.subscribeJudge(id)
      this.reloadFirstPage()
    },
    page() {
      this.load()
    },
    'filters.unlinked'() {
      this.reloadFirstPage()
    },
    reloadKey() {
      this.load()
    },
  },
  mounted() {
    this.offMessage = onMessage(this.onWsMessage)
    this.offStatus = onStatus(this.onWsStatus)
    if (this.judgeId) {
      this.subscribeJudge(this.judgeId)
      this.load()
    } else {
      this.$nextTick(() => this.$refs.input && this.$refs.input.focus())
    }
  },
  beforeDestroy() {
    this.subscribeJudge(null)
    this.offMessage && this.offMessage()
    this.offStatus && this.offStatus()
  },
  methods: {
    battleTitle,
    campByNumber,
    campShortName,
    formatDateTime,
    formatGameTime,
    lastUpdateAt,
    stateLabel,
    stateVariant,
    syncStatus,
    timeAgo,

    submit() {
      const id = parseJudgeId(this.input)
      if (!id) {
        this.inputError = 'Введите числовой игровой ID судьи'
        return
      }
      this.inputError = ''
      if (id === this.judgeId) this.load()
      else this.$emit('search', id)
    },

    useJudge(id) {
      this.input = String(id)
      this.submit()
    },

    focusInput() {
      const el = this.$refs.input
      if (el) {
        el.focus()
        el.select()
      }
    },

    // смена страницы сама перечитает список — второй запрос не нужен
    reloadFirstPage() {
      if (this.page !== 1) this.page = 1
      else this.load()
    },

    subscribeJudge(judgeId) {
      const next = judgeId ? judgeBattlesChannel(judgeId) : null
      if (next === this.channel) return
      if (this.channel) unsubscribe(this.channel)
      this.channel = next
      if (next) {
        connectToWs()
        subscribe(next)
      }
    },

    onWsStatus(status) {
      // пока сокета не было, изменения прошли мимо — один раз перечитываем список
      if (status === 'open' && this.loaded && this.judgeId) this.load({silent: true})
    },

    onWsMessage(msg) {
      // ["Access denied"] и чужие каналы пропускаем
      if (!msg || Array.isArray(msg) || msg.event !== JUDGE_BATTLE_UPDATED_EVENT) return
      if (!this.channel || (msg.channel && msg.channel !== this.channel)) return
      const b = msg.data?.mlbb_battle
      if (!b || !b.id) return
      this.upsertBattle(b)
      // странице это нужно для игр серии — отдельно их больше не опрашиваем
      this.$emit('battle-updated', b)
    },

    upsertBattle(b) {
      const idx = this.battles.findIndex((x) => x.id === b.id)
      // «только непривязанные» фильтрует бэк: привязанный батл из такого списка уходит
      const hidden = this.filters.unlinked && b.group_id
      if (idx >= 0) {
        if (hidden) {
          this.battles.splice(idx, 1)
          this.total = Math.max(0, this.total - 1)
        } else {
          this.$set(this.battles, idx, {...this.battles[idx], ...b})
        }
      } else if (!hidden && this.page === 1) {
        // новый батл судьи — наверх, как и в списке бэка (новые сверху)
        this.battles.unshift(b)
        this.total += 1
      }
    },

    async load({silent = false} = {}) {
      if (!this.judgeId) return
      const judgeId = this.judgeId
      if (!silent) this.loading = true
      try {
        const res = await api.mlbbEsports.battles({
          judge_id: judgeId,
          page: this.page,
          unlinked: this.filters.unlinked ? 1 : undefined,
        }, {skipErrorToast: silent})
        if (judgeId !== this.judgeId) return
        const meta = res?.data?.meta || {}
        this.battles = res?.data?.data || []
        this.total = Number(meta.total) || this.battles.length
        this.perPage = Number(meta.per_page) || 30
        this.lastPage = Number(meta.last_page) || 1
        this.loadError = ''
        this.loaded = true
        this.$emit('loaded', this.battles)
      } catch (e) {
        if (!silent) this.loadError = e?.response?.data?.message || e?.message || 'ошибка сети'
      } finally {
        this.loading = false
      }
    },

    async unlink(b) {
      const ok = await this.$bvModal.msgBoxConfirm(
          `Снять привязку игры «${battleTitle(b)}» к матчу #${b.group_id}? Команды снова будут показываться по игровым названиям.`,
          {title: 'Отвязать от матча', okTitle: 'Отвязать', cancelTitle: 'Отмена', okVariant: 'danger', centered: true},
      )
      if (!ok) return
      this.busyId = b.id
      try {
        await api.mlbbEsports.unlinkBattle(b.id)
        this.$emit('changed', b.id)
        await this.load()
      } catch (e) {
        // тост покажет интерсептор
      } finally {
        this.busyId = null
      }
    },

    async resync(b) {
      this.busyId = b.id
      try {
        await api.mlbbEsports.resyncBattle(b.id)
        this.$emit('changed', b.id)
        await this.load()
      } catch (e) {
        // тост покажет интерсептор
      } finally {
        this.busyId = null
      }
    },

    async saveJudge() {
      this.savingJudge = true
      try {
        await api.mlbbEsports.storeJudge({judge_id: this.judgeId, name: `Судья ${this.judgeId}`, is_active: true})
        this.$emit('judges-changed')
      } catch (e) {
        // тост покажет интерсептор (422 — уже есть в справочнике)
      } finally {
        this.savingJudge = false
      }
    },
  },
}
</script>

<style lang="scss">
.mb-conn {
  &__form {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    flex-wrap: wrap;
  }

  &__id {
    flex: 0 1 280px;

    .mb-input { height: 42px; font-size: 17px; letter-spacing: .04em; }
  }

  &__recent {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  &__judge {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    font-size: 13px;
  }

  &__toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;

    .mb-input { flex: 0 1 260px; min-width: 180px; }
  }

  &__teams { max-width: 320px; }
  &__sync { max-width: 260px; }

  &__pager { display: flex; justify-content: flex-end; }

}
</style>
